// Karthus Requiem VFX:
//   RequiemWindupVFX — dark channeling aura on Karthus while casting
//   RequiemBeamVFX   — delayed beam descending from sky, tracks the unit's live position

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

// ── Windup VFX ────────────────────────────────────────────────────────────────

const WINDUP_TOTAL = 900;
const PARTICLE_COUNT = 14;

interface WindupParticle {
  angle: number;
  radius: number;
  speed: number; // radians/ms
  size: number;
  alpha: number;
  riseY: number;    // current upward offset
  riseSpeed: number;
}

export class RequiemWindupVFX implements Animation {
  private elapsed = 0;
  private particles: WindupParticle[];
  onEnd?: () => void;

  constructor(
    private center: Point,
    private hexSize: number,
  ) {
    this.particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      angle: (i / PARTICLE_COUNT) * Math.PI * 2,
      radius: hexSize * (0.5 + Math.random() * 0.8),
      speed: 0.0008 + Math.random() * 0.0006,
      size: 2 + Math.random() * 3,
      alpha: 0.4 + Math.random() * 0.5,
      riseY: 0,
      riseSpeed: 0.02 + Math.random() * 0.03,
    }));
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, WINDUP_TOTAL);
    for (const p of this.particles) {
      p.angle += p.speed * dt;
      p.riseY += p.riseSpeed * dt;
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed / WINDUP_TOTAL;
    const fadeIn = Math.min(this.elapsed / 300, 1);
    const fadeOut = t > 0.85 ? 1 - (t - 0.85) / 0.15 : 1;
    const baseAlpha = fadeIn * fadeOut;

    ctx.save();
    ctx.translate(this.center.x, this.center.y);

    // Dark shadow aura on the ground
    const auraR = this.hexSize * (1.0 + t * 0.6);
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, auraR);
    grad.addColorStop(0, `rgba(60, 0, 100, ${baseAlpha * 0.7})`);
    grad.addColorStop(0.55, `rgba(30, 0, 60, ${baseAlpha * 0.4})`);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(0, 0, auraR, auraR * 0.45, 0, 0, Math.PI * 2);
    ctx.fill();

    // Pulsing outer ring
    const pulse = 0.8 + Math.sin(this.elapsed * 0.009) * 0.2;
    ctx.globalAlpha = baseAlpha * pulse * 0.65;
    ctx.strokeStyle = "#7c3aed";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#a855f7";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(0, 0, auraR * 0.85, 0, Math.PI * 2);
    ctx.stroke();

    // Ghostly particles orbiting and rising
    for (const p of this.particles) {
      const px = Math.cos(p.angle) * p.radius;
      const py = Math.sin(p.angle) * p.radius * 0.5 - p.riseY;
      const lifeAlpha = Math.max(0, 1 - p.riseY / (this.hexSize * 1.2));
      ctx.globalAlpha = baseAlpha * p.alpha * lifeAlpha;
      ctx.fillStyle = "#c4b5fd";
      ctx.shadowColor = "#7c3aed";
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(px, py, p.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= WINDUP_TOTAL;
  }
}

// ── Beam VFX ──────────────────────────────────────────────────────────────────

const BEAM_FALL_MS = 280;
const IMPACT_MS    = 380;
const BEAM_TOTAL   = BEAM_FALL_MS + IMPACT_MS;

export class RequiemBeamVFX implements Animation {
  private totalElapsed = 0;
  private elapsed = 0;
  private impactFired = false;
  // Snapshot the position at impact time so the explosion stays put after landing
  private impactPos: Point | null = null;
  onEnd?: () => void;

  constructor(
    private getTargetPos: () => Point,  // live unit position — tracks movement during windup
    private startY: number,             // top of canvas (typically 0)
    private hexSize: number,
    private delayMs: number,
    private onImpact?: () => void,
  ) {}

  update(dt: number): void {
    this.totalElapsed += dt;
    if (this.totalElapsed < this.delayMs) return;
    this.elapsed = Math.min(this.elapsed + dt, BEAM_TOTAL);

    if (!this.impactFired && this.elapsed >= BEAM_FALL_MS) {
      this.impactFired = true;
      this.impactPos = this.getTargetPos();
      this.onImpact?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.totalElapsed < this.delayMs) return;

    const t = this.elapsed;
    // During fall: track the live unit; after impact: stay at the snapshotted position
    const livePos = this.impactPos ?? this.getTargetPos();
    const tx = livePos.x;
    const ty = livePos.y;

    // Phase 1: beam descends from startY to targetPos
    if (t < BEAM_FALL_MS) {
      const fallT = t / BEAM_FALL_MS;
      const currentY = this.startY + (ty - this.startY) * Easing.easeOutCubic(fallT);
      const beamHeight = currentY - this.startY;

      if (beamHeight > 0) {
        ctx.save();

        // Outer glow beam
        ctx.globalAlpha = 0.6 + fallT * 0.2;
        ctx.fillStyle = "#9966ff";
        ctx.shadowColor = "#c084fc";
        ctx.shadowBlur = 24;
        ctx.fillRect(tx - 6, this.startY, 12, beamHeight);

        // White core
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = "#ffffff";
        ctx.shadowBlur = 8;
        ctx.fillRect(tx - 2.5, this.startY, 5, beamHeight);

        // Diamond head at beam bottom
        ctx.translate(tx, currentY);
        ctx.shadowColor = "#ffffff";
        ctx.shadowBlur = 20;
        ctx.fillStyle = "#e0c0ff";
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.moveTo(0, -10);
        ctx.lineTo(8, 0);
        ctx.lineTo(0, 6);
        ctx.lineTo(-8, 0);
        ctx.closePath();
        ctx.fill();

        ctx.restore();
      }
    }

    // Phase 2: impact explosion
    if (t >= BEAM_FALL_MS) {
      const impT = (t - BEAM_FALL_MS) / IMPACT_MS;
      const alpha = 1 - impT;

      ctx.save();

      // Pillar flash fades out quickly
      const pillarAlpha = Math.max(0, 1 - impT / 0.22);
      if (pillarAlpha > 0) {
        ctx.globalAlpha = pillarAlpha * 0.5;
        ctx.fillStyle = "#9966ff";
        ctx.shadowColor = "#c084fc";
        ctx.shadowBlur = 20;
        ctx.fillRect(tx - 6, this.startY, 12, ty - this.startY);
      }

      // Outer purple shockwave ring
      const outerR = this.hexSize * 2.2 * Easing.easeOutCubic(impT);
      ctx.globalAlpha = alpha * 0.9;
      ctx.strokeStyle = "#9966ff";
      ctx.lineWidth = 5 * (1 - impT) + 1;
      ctx.shadowColor = "#c084fc";
      ctx.shadowBlur = 28;
      ctx.beginPath();
      ctx.arc(tx, ty, outerR, 0, Math.PI * 2);
      ctx.stroke();

      // Inner gold ring
      const innerR = outerR * 0.52;
      ctx.globalAlpha = alpha * 0.75;
      ctx.strokeStyle = "#eab308";
      ctx.lineWidth = 2.5;
      ctx.shadowColor = "#ffd700";
      ctx.shadowBlur = 16;
      ctx.beginPath();
      ctx.arc(tx, ty, innerR, 0, Math.PI * 2);
      ctx.stroke();

      // White core flash
      const flashR = this.hexSize * 0.85 * (1 - Easing.easeInQuad(impT));
      if (flashR > 0.5) {
        ctx.globalAlpha = alpha * 0.85;
        ctx.fillStyle = "#ffffff";
        ctx.shadowColor = "#ffffff";
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.arc(tx, ty, flashR, 0, Math.PI * 2);
        ctx.fill();
      }

      // Radial sparks
      const sparkCount = 10;
      ctx.shadowBlur = 10;
      for (let i = 0; i < sparkCount; i++) {
        const angle = (i / sparkCount) * Math.PI * 2 + impT;
        const sparkR = this.hexSize * 2.0 * Easing.easeOutCubic(impT);
        const isGold = i % 2 === 0;
        ctx.globalAlpha = alpha * 0.65;
        ctx.strokeStyle = isGold ? "#eab308" : "#9966ff";
        ctx.shadowColor = isGold ? "#ffd700" : "#c084fc";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(tx + Math.cos(angle) * innerR * 0.5, ty + Math.sin(angle) * innerR * 0.5);
        ctx.lineTo(tx + Math.cos(angle) * sparkR * 0.85, ty + Math.sin(angle) * sparkR * 0.85);
        ctx.stroke();
      }

      ctx.restore();
    }
  }

  isDone(): boolean {
    return this.totalElapsed >= this.delayMs + BEAM_TOTAL;
  }
}
